import {
  CreateCrmIntegration,
  CrmIntegration,
  CrmProviderType,
  CrmSegment,
  OAuthUrlResponse,
  ReadAdsAccount,
  ReadAdsCampaign,
  ReadAdsCampaignMetric,
  ReadCrmAuthInfo,
  UpdateCrmAuthInfo,
  UpdateCrmIntegrationSettings,
} from '@tinct/core-api-models';
import { snakeCase } from 'change-case';
import { ApiClient } from '../api-client/api-client';
import {
  ApiCollectionResponse,
  FieldQuery,
  GetCollectionQueryParams,
} from '../types';

export type GetOAuthUrlParams = {
  organizationId: string;
  provider: CrmProviderType;
  /** Where the OAuth callback should return the user (popup flow). */
  targetUrl?: string;
};

export type GetCrmIntegrationsParams = GetCollectionQueryParams<CrmIntegration> & {
  filters?: {
    organizationId?: string;
  };
};

export type GetCrmSegmentsParams = GetCollectionQueryParams<CrmSegment> & {
  integrationId: string;
  forceSync?: boolean;
};

export type GetAdsCampaignsParams = GetCollectionQueryParams<ReadAdsCampaign> & {
  integrationId: string;
};

export type GetAdsCampaignMetricsParams = GetCollectionQueryParams<ReadAdsCampaignMetric> & {
  integrationId: string;
  adsCampaignId: string;
  /** Inclusive `YYYY-MM-DD`; defaults server-side to the last 30 days. */
  from?: string;
  /** Inclusive `YYYY-MM-DD`; defaults server-side to today (UTC). */
  to?: string;
};

export class CrmIntegrationsEndpoint {
  constructor(private client: ApiClient) {}

  /**
   * Get the OAuth authorization URL for a CRM provider.
   * The frontend should open this URL in a popup.
   */
  async getOAuthUrl(params: GetOAuthUrlParams): Promise<OAuthUrlResponse> {
    return this.client.post<OAuthUrlResponse>(
      '/api/v1/crm-integrations/oauth-url',
      params
    );
  }

  /**
   * Connect a CRM provider using direct credentials.
   * Used by providers that authenticate with credentials (e.g. RudderStack)
   * rather than the OAuth flow.
   */
  async createCrmIntegration(
    params: CreateCrmIntegration
  ): Promise<CrmIntegration> {
    return this.client.post<CrmIntegration>('/api/v1/crm-integrations', params);
  }

  /**
   * Get all CRM integrations for an organization.
   */
  async getCrmIntegrations(
    params: GetCrmIntegrationsParams
  ): Promise<ApiCollectionResponse<CrmIntegration>> {
    const { fields, offset, limit, filters, sort } = params;
    return this.client.getCollection<CrmIntegration>(
      '/api/v1/crm-integrations',
      fields,
      {
        offset,
        limit,
        ...filters,
        sort,
      }
    );
  }

  /**
   * Get a CRM integration by ID.
   */
  async getCrmIntegration(
    integrationId: string,
    fields?: FieldQuery<CrmIntegration>
  ): Promise<CrmIntegration> {
    return this.client.getById<CrmIntegration>(
      `/api/v1/crm-integrations/${integrationId}`,
      fields
    );
  }

  /**
   * Get the non-secret auth info (configuration) for an integration.
   * Credential/token fields are never exposed; the RudderStack write key is
   * returned masked (all but its last 3 characters replaced with `*`).
   */
  async getCrmIntegrationAuthInfo(
    integrationId: string
  ): Promise<ReadCrmAuthInfo> {
    return this.client.getById<ReadCrmAuthInfo>(
      `/api/v1/crm-integrations/${integrationId}/auth-info`
    );
  }

  /**
   * Update the (non-OAuth) auth info of an existing integration. The write key
   * is optional: when omitted the key already stored for the integration is
   * reused.
   */
  async updateCrmIntegrationAuthInfo(
    integrationId: string,
    params: UpdateCrmAuthInfo
  ): Promise<ReadCrmAuthInfo> {
    return this.client.put<ReadCrmAuthInfo>(
      `/api/v1/crm-integrations/${integrationId}/auth-info`,
      params
    );
  }

  /**
   * Disconnect (delete) an integration.
   */
  async deleteCrmIntegration(integrationId: string): Promise<void> {
    return this.client.delete<void>(`/api/v1/crm-integrations/${integrationId}`);
  }

  /**
   * List segments (company lists) from a CRM integration.
   * If the last sync was more than 24 hours ago, triggers a new sync automatically.
   */
  async getCrmSegments(
    params: GetCrmSegmentsParams
  ): Promise<ApiCollectionResponse<CrmSegment>> {
    const { integrationId, fields, offset, limit, forceSync } = params;
    return this.client.getCollection<CrmSegment>(
      `/api/v1/crm-integrations/${integrationId}/segments`,
      fields,
      {
        offset,
        limit,
        force_sync: forceSync,
      }
    );
  }

  /**
   * Synced ad campaigns (Google Ads or LinkedIn Ads) of an integration, with
   * their matches.
   */
  async getAdsCampaigns(
    params: GetAdsCampaignsParams
  ): Promise<ApiCollectionResponse<ReadAdsCampaign>> {
    const { integrationId, fields, offset, limit } = params;
    return this.client.getCollection<ReadAdsCampaign>(
      `/api/v1/crm-integrations/${integrationId}/ads-campaigns`,
      fields,
      { offset, limit }
    );
  }

  /**
   * Ad accounts readable through an ads integration (live call to the
   * provider). `selected` reflects the stored settings; an empty selection
   * means every account and every row reads `selected: true`.
   */
  async getAdsAccounts(integrationId: string): Promise<ReadAdsAccount[]> {
    return this.client.getById<ReadAdsAccount[]>(
      `/api/v1/crm-integrations/${integrationId}/ads-accounts`
    );
  }

  /**
   * Update the per-provider settings of an integration (LinkedIn Ads: the ad
   * accounts to sync). `adAccountIds` omitted keeps the stored selection,
   * `[]` syncs every account. The change applies to the next sync only.
   */
  async updateCrmIntegrationSettings(
    integrationId: string,
    settings: UpdateCrmIntegrationSettings
  ): Promise<CrmIntegration> {
    return this.client.put<CrmIntegration>(
      `/api/v1/crm-integrations/${integrationId}/settings`,
      settings,
      { params: { fields: 'id,settings' } }
    );
  }

  /**
   * Daily metrics of one synced ad campaign, `from`/`to` inclusive
   * (`YYYY-MM-DD`). Default window: last 30 days; default sort: metric date
   * descending.
   */
  async getAdsCampaignMetrics(
    params: GetAdsCampaignMetricsParams
  ): Promise<ApiCollectionResponse<ReadAdsCampaignMetric>> {
    const { integrationId, adsCampaignId, fields, offset, limit, from, to, sort } =
      params;
    return this.client.getCollection<ReadAdsCampaignMetric>(
      `/api/v1/crm-integrations/${integrationId}/ads-campaigns/${adsCampaignId}/metrics`,
      fields,
      {
        offset,
        limit,
        from,
        to,
        // Sort field names travel snake_case on the wire (`metric_date`).
        sort: sort
          ? { field: snakeCase(String(sort.field)), direction: sort.direction }
          : undefined,
      }
    );
  }
}
